import React, { createContext, useCallback, useContext, useState } from 'react';
import { runNightlyJob as runNightlyJobApi } from '../api/guru';

// The "Run Nightly Job Now" button on the Projects page re-checks every
// registered project's connector access live — for an org with a lot of
// projects that's a real, possibly multi-minute, single HTTP request (see
// app/services/projects/scheduler.py's run_nightly_job()). Dev, 2026-07-03:
// its "running…" state used to be local useState inside Projects.js, so
// navigating to /chat or /admin and back unmounted the page and threw that
// state away — the request kept running on the backend the whole time (see
// ChatContext.js's comment for why unmounting doesn't cancel anything), but
// the button reset to its idle label and there was no way to tell it had
// finished, or what happened, without leaving it clicked and staring at the
// tab. Moving `running`/`lastResult` up here (mounted once, above the
// router, same pattern as ChatContext) means coming back to Projects shows
// the real current state — still running, or the outcome of the run that
// just finished while you were elsewhere.
const NightlyJobContext = createContext(null);

export function NightlyJobProvider({ children }) {
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null); // { ok, message, at }

  const runNow = useCallback(async () => {
    setRunning(true);
    try {
      const { data } = await runNightlyJobApi();
      const driftCount = (data.drift && data.drift.drift_count) || 0;
      setLastResult({
        ok: true,
        message: `✅ Nightly job ran — ${data.projects_checked ?? 0} project(s) checked, ${driftCount} drift alert(s).`,
        at: Date.now(),
      });
    } catch {
      setLastResult({ ok: false, message: "Could not run the nightly job.", at: Date.now() });
    } finally {
      setRunning(false);
    }
  }, []);

  return (
    <NightlyJobContext.Provider value={{ running, lastResult, runNow }}>
      {children}
    </NightlyJobContext.Provider>
  );
}

export function useNightlyJob() {
  const ctx = useContext(NightlyJobContext);
  if (!ctx) throw new Error('useNightlyJob must be used inside <NightlyJobProvider>');
  return ctx;
}
